import React, { CSSProperties, useCallback, useMemo, useState } from "react";

import {
  Button,
  Form,
  Icon,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import moment from "moment";

import EditorColumn from "react-lib/appcon/common/EditorColumn";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";
import ModRehabOrderLog from "./ModRehabOrderLog";

import LoadingIcon from "../HCU/LoadingIcon";

import { formatRehabTreatmentPerform, RehabOrderBillType, RehabOrderType } from "./RHBInterface";

import { formatDatetime, getOrdinal } from "react-lib/utils/dateUtils";

import "./TableTreatment.scss";
import formatComma from "react-lib/utils/formatComma";

type TableTreatmentPerformProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  rehabOrderActionLogList?: any[];
  rehabTreatmentPerform: RehabOrderType | null;
  rehabTreatmentPerformList?: RehabOrderType[];
  buttonLoadCheck: any;
  searchedItemListWithKey: any;
  onCancelOrder: (data: Record<string, any>) => void;
  onDeleteOrderItem: (id: number, btnAction: string) => void;
  onUpdateTreatmentCode: (data: {
    action: string;
    id?: number;
    btnAction: string;
    index: number;
    order: number | null;
    product?: number | null;
    quantity?: string;
  }) => void;
};

type Styles = Record<"button", CSSProperties>;

const styles: Styles = {
  button: {
    fontSize: "0.6rem",
    height: "1.25rem",
    padding: "0",
    width: "1.25rem",
  },
};

const BUTTON_ACTIONS = {
  add_product: "ADD_PRODUCT",
  delete: "DELETE",
  delete_product: "DELETE_PRODUCT",
  edit_product: "EDIT_PRODUCT",
  edit_qty: "EDIT_QTY",
};

const TABLE_TREATMENT_PERFORM = "TableTreatmentPerform";

const ACTION_DELETE = `${TABLE_TREATMENT_PERFORM}_${BUTTON_ACTIONS.delete}`;
const ACTION_EDIT_PRODUCT = `${TABLE_TREATMENT_PERFORM}_${BUTTON_ACTIONS.edit_product}`;
const ACTION_EDIT_QTY = `${TABLE_TREATMENT_PERFORM}_${BUTTON_ACTIONS.edit_qty}`;
const ACTION_DELETE_PRODUCT = `${TABLE_TREATMENT_PERFORM}_${BUTTON_ACTIONS.delete_product}`;
const ACTION_ADD_PRODUCT = `${TABLE_TREATMENT_PERFORM}_${BUTTON_ACTIONS.add_product}`;

const TableTreatmentPerform = (props: TableTreatmentPerformProps) => {
  const [openModLog, setOpenModLog] = useState<any>({open: false, orderId: null});

  const handleSelect = useCallback((order: RehabOrderType) => {
    const formattedData = formatRehabTreatmentPerform(order);

    props.setProp("rehabTreatmentPerform", formattedData);

    props.setProp("userTokenize", null);
  }, []);

  const handleUpdateTreatmentCode = useCallback(
    (data: Record<string, any>) => {
      const updateData = {
        action: data.action,
        id: data.bill.id,
        btnAction: `${data.btnAction}_${data.order}_${data.index}`,
        index: data.index,
        order: data.order,
        product: data.bill.product,
        quantity: data.bill.quantity,
      };

      updateData[data.key as "product" | "quantity"] = data.value;

      props.onUpdateTreatmentCode(updateData);
    },
    [props.onUpdateTreatmentCode]
  );

  // Flatten data structure
  const flattenedData = useMemo(() => {
    const result: (RehabOrderType & {
      bill: RehabOrderBillType;
      billIndex: number;
      order_id: number;
    })[] = [];

    for (const order of props.rehabTreatmentPerformList ?? []) {
      // แต่ละแถวแทนหนึ่ง bill item
      for (const [billIndex, bill] of order.bill_items.entries()) {
        result.push({
          ...order,
          bill,
          billIndex,
          order_id: order.id,
        });
      }
    }

    return result;
  }, [props.rehabTreatmentPerformList]);

  const renderTreatmentOrder = useCallback(
    (order: RehabOrderType) => (
      <div>
        {order.items.map((item, index) => (
          <div
            key={item.id}
            style={{
              display: "grid",
              gridTemplateColumns: "1.5rem 1fr",
              marginBottom: index < order.items.length - 1 ? "0.25rem" : "0",
            }}
          >
            {order.order_perform_time ? (
              <label />
            ) : (
              <LoadingIcon
                color="red"
                loading={!!props.buttonLoadCheck?.[`${ACTION_DELETE}_${item.id}`]}
                name="minus circle"
                onClick={() => {
                  if (order.items.length === 1) {
                    props.onCancelOrder(order);

                    return;
                  }

                  props.onDeleteOrderItem(item.id, `${ACTION_DELETE}_${item.id}`);
                }}
              />
            )}
            <label>{item.plan_item_detail?.name}</label>
          </div>
        ))}
      </div>
    ),
    [props.buttonLoadCheck, props.onCancelOrder, props.onDeleteOrderItem]
  );

  const renderOrderNumbers = useCallback(
    (order: RehabOrderType) => (
      <div>
        {order.items.map((item, index) => {
          const ordinal = getOrdinal(item.no);

          return (
            <div
              key={item.id}
              style={{
                marginBottom: index < order.items.length - 1 ? "0.5rem" : "0",
                textAlign: "center",
              }}
            >
              {ordinal.number}
              <sup>{ordinal.suffix}</sup>
            </div>
          );
        })}
      </div>
    ),
    []
  );

  const handleCloseModLog = () => {
    props.setProp('rehabOrderActionLogList', []);
    setOpenModLog({open: false, orderId: null});
  };

  return (
    <>
      <Table id="TableTreatmentPerform" celled compact structured>
        <TableHeader className="rehab-treatment">
          <TableRow>
            <TableHeaderCell textAlign="center" width={2}>
              Perform date
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={3}>
              Treatment order
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={1}>
              No.
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={3}>
              Perform by
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={3}>
              Treatment code
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={1}>
              Price/unit
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={1}>
              Qty
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={1}>
              Detail
            </TableHeaderCell>
            <TableHeaderCell textAlign="center" width={1} />
          </TableRow>
        </TableHeader>

        <TableBody>
          {flattenedData.length === 0 ? (
            <TableCell style={{ color: "grey" }} colSpan="9" textAlign="center">
              No rows found
            </TableCell>
          ) : null}

          {flattenedData.map((row, index) => {
            // ตรวจสอบว่าเป็นแถวแรกของ order หรือไม่
            const isFirstRowForOrder =
              index === 0 || flattenedData[index - 1].order_id !== row.order_id;

            // นับจำนวนแถวทั้งหมดของ order นี้ (= จำนวน bill items)
            const rowsForThisOrder = flattenedData.filter(
              (d) => d.order_id === row.order_id
            ).length;

            const isSelected = props.rehabTreatmentPerform?.id === row.order_id;

            return (
              <TableRow key={index} className={isSelected ? "blueSelectedRow" : ""}>
                {/* Perform Date - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} textAlign="center" verticalAlign="top">
                    <div>
                      {row.order_perform_time
                        ? formatDatetime(moment(row.start))
                        : formatDatetime(moment(row.created))}
                    </div>
                  </TableCell>
                )}

                {/* Treatment Order - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} verticalAlign="top">
                    {renderTreatmentOrder(row)}
                  </TableCell>
                )}

                {/* No. - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} verticalAlign="top">
                    {renderOrderNumbers(row)}
                  </TableCell>
                )}

                {/* Perform By - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} verticalAlign="top">
                    <div>{row.order_perform_by_fullname}</div>
                  </TableCell>
                )}

                {/* Treatment Code - แยกแต่ละ bill */}
                <TableCell verticalAlign="top">
                  <Form
                    className="--override-disabled --grey"
                    style={{ alignItems: "center", display: "flex" }}
                    size="mini"
                  >
                    <div style={{ display: "flex", width: "100%" }}>
                      {row.bill.is_paid && (
                        <Icon
                          style={{ fontSize: "1rem", marginLeft: "-0.5rem", marginTop: "0.4rem" }}
                          color="green"
                          name="check circle"
                        />
                      )}
                      <SearchBoxWithKey
                        onEvent={props.onEvent}
                        id={`TTP_${row.order_id}_${row.billIndex}`}
                        defaultText={row.bill.product_name || ""}
                        disabled={row.bill.is_paid}
                        selectedItem={row.bill.product}
                        type="Treatment"
                        inline
                        searchedItemListWithKey={props.searchedItemListWithKey}
                        setSelected={(value) => {
                          handleUpdateTreatmentCode({
                            action: row.bill.order ? "EDIT" : "REQUEST",
                            bill: row.bill,
                            btnAction: ACTION_EDIT_PRODUCT,
                            index: row.billIndex,
                            key: "product",
                            order: row.order_id,
                            value,
                          });
                        }}
                        loading={
                          !!props.buttonLoadCheck?.[
                            `${ACTION_EDIT_PRODUCT}_${row.order_id}_${row.billIndex}`
                          ]
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.85rem 1.85rem",
                        margin: "0 -0.5rem 0 0.5rem",
                      }}
                    >
                      <Button
                        style={styles.button}
                        color="red"
                        disabled={row.bill.is_paid}
                        icon="minus"
                        size="mini"
                        onClick={() => {
                          if (!row.bill.id) {
                            return;
                          }

                          handleUpdateTreatmentCode({
                            action: "DELETE",
                            bill: row.bill,
                            btnAction: ACTION_DELETE_PRODUCT,
                            index: row.billIndex,
                            order: row.order_id,
                          });
                        }}
                        loading={
                          !!props.buttonLoadCheck?.[
                            `${ACTION_DELETE_PRODUCT}_${row.order_id}_${row.billIndex}`
                          ]
                        }
                      />
                      {row.billIndex === row.bill_items.length - 1 && (
                        <Button
                          style={styles.button}
                          color="green"
                          disabled={!row.bill.id}
                          icon="plus"
                          size="mini"
                          onClick={() => {
                            handleUpdateTreatmentCode({
                              action: "ADD",
                              bill: row.bill,
                              btnAction: ACTION_ADD_PRODUCT,
                              index: row.billIndex,
                              order: row.order_id,
                            });
                          }}
                          loading={
                            !!props.buttonLoadCheck?.[
                              `${ACTION_ADD_PRODUCT}_${row.order_id}_${row.billIndex}`
                            ]
                          }
                        />
                      )}
                    </div>
                  </Form>
                </TableCell>

                {/* Price - แยกแต่ละ bill */}
                <TableCell textAlign="center" verticalAlign="top">
                  <Form style={{ textAlign: "right" }} size="mini">
                    <Input
                      className="price-unit"
                      value={row.bill.price ? formatComma(String(row.bill.price)) : ""}
                      readOnly
                    />
                  </Form>
                </TableCell>

                {/* Quantity - แยกแต่ละ bill */}
                <TableCell textAlign="center" verticalAlign="top">
                  <Form className="--override-disabled --grey" size="mini">
                    <EditorColumn
                      align="left"
                      disabled={row.bill.is_paid}
                      inputMin={0}
                      inputSize="mini"
                      inputStyle={{ minWidth: "4.5rem" }}
                      inputType="number"
                      value={row.bill.quantity}
                      editing
                      inputNumber
                      valueOnFocus
                      withoutStyle
                      onDataChanged={(value: string) => {
                        if (Number(value) === row.bill.quantity) {
                          return;
                        }

                        handleUpdateTreatmentCode({
                          action: row.bill.order ? "EDIT" : "REQUEST",
                          bill: row.bill,
                          btnAction: ACTION_EDIT_QTY,
                          index: row.billIndex,
                          key: "quantity",
                          order: row.order_id,
                          value: Number(value),
                        });
                      }}
                      loading={
                        !!props.buttonLoadCheck?.[
                          `${ACTION_EDIT_QTY}_${row.order_id}_${row.billIndex}`
                        ]
                      }
                    />
                  </Form>
                </TableCell>

                {/* Detail - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} textAlign="center" verticalAlign="top">
                    <div style={{ display: "grid", placeContent: "center" }}>
                      <Icon
                        color="blue"
                        size="large"
                        link
                        onClick={() => {
                          handleSelect(row);
                        }}
                        name={
                          props.rehabTreatmentPerform?.id === row.order_id
                            ? "folder open"
                            : "folder outline"
                        }
                      />
                    </div>
                  </TableCell>
                )}

                {/* Action - merge สำหรับทุก bill ของ order นี้ */}
                {isFirstRowForOrder && (
                  <TableCell rowSpan={rowsForThisOrder} textAlign="center" verticalAlign="top">
                    <div>
                      {!row.order_perform_time && (
                        <Button
                          style={{ fontSize: "0.55rem" }}
                          color="red"
                          onClick={() => {
                            props.onCancelOrder(row);
                          }}
                        >
                          CANCEL
                        </Button>
                      )}
                      {row.status_name === "PERFORMED" && (
                        <Button
                          style={{ padding: "0.45rem" }}
                          icon="history"
                          size="medium"
                          onClick={() => {
                            setOpenModLog({open: true, orderId: row?.id})
                          }}
                        />
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Modal open={openModLog.open} size="small" closeIcon onClose={handleCloseModLog}>
        <ModRehabOrderLog 
          onEvent={props.onEvent}
          rehabOrderActionLogList={props.rehabOrderActionLogList}
          orderId={openModLog.orderId}
        />
        </Modal>
    </>
  );
};

TableTreatmentPerform.displayName = "TableTreatmentPerform";

export default React.memo(TableTreatmentPerform);
